import React, { useState, useRef } from 'react';
import { jobsAPI } from '../../utils/api';
import toastr from 'toastr';
import './AddCandidateModal.css';

const SOURCE_OPTIONS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'naukri', label: 'Naukri' },
  { value: 'indeed', label: 'Indeed' },
  { value: 'referral', label: 'Referral' },
  { value: 'campus', label: 'Campus Hiring' },
  { value: 'job_board', label: 'Job Board' },
  { value: 'manual', label: 'Direct / Sourced' },
  { value: 'other', label: 'Other' }
];

const NOTICE_PERIOD_OPTIONS = [
  'Immediate', '15 Days', '30 Days', '45 Days', '60 Days', '90 Days', 'More than 90 Days'
];

const AddCandidateDrawerContent = ({ jobId, jobTitle, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    linkedinUrl: '',
    naukriUrl: '',
    githubUrl: '',
    portfolioUrl: '',
    source: 'linkedin',
    referredBy: '',
    currentCompany: '',
    currentTitle: '',
    experienceYears: '',
    noticePeriod: '',
    currentCTC: '',
    expectedSalary: '',
    resumeUrl: '',
    notes: '',
    tags: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showAdditionalFields, setShowAdditionalFields] = useState(false);

  // Resume file upload state
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadedResume, setUploadedResume] = useState(null); // { fileUrl, filename }
  const fileInputRef = useRef(null);

  // Notify recipients state
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [notifyEmailInput, setNotifyEmailInput] = useState('');
  const [notifyEmails, setNotifyEmails] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleResumeFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResumeFile(file);
    setUploadingResume(true);
    try {
      const fd = new FormData();
      fd.append('resume', file);
      const res = await jobsAPI.uploadCandidateResume(fd);
      setUploadedResume({ fileUrl: res.data.fileUrl, filename: res.data.filename });
      toastr.success('Resume uploaded');
    } catch (err) {
      toastr.error('Resume upload failed. You can still provide a URL below.');
      setResumeFile(null);
    } finally {
      setUploadingResume(false);
    }
  };

  const addNotifyEmail = () => {
    const email = notifyEmailInput.trim();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;
    if (!notifyEmails.includes(email)) {
      setNotifyEmails(prev => [...prev, email]);
    }
    setNotifyEmailInput('');
  };


  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.linkedinUrl.trim()) newErrors.linkedinUrl = 'LinkedIn profile is required';
    else if (!formData.linkedinUrl.includes('linkedin.com')) newErrors.linkedinUrl = 'Must be a valid LinkedIn URL';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      // Determine final resume URL: prefer uploaded file, fallback to manual URL
      const finalResumeUrl = uploadedResume?.fileUrl || formData.resumeUrl.trim() || undefined;
      const finalResumeFilename = uploadedResume?.filename || undefined;

      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        location: formData.location.trim() || undefined,
        linkedinUrl: formData.linkedinUrl.trim() || undefined,
        naukriUrl: formData.naukriUrl.trim() || undefined,
        githubUrl: formData.githubUrl.trim() || undefined,
        portfolioUrl: formData.portfolioUrl.trim() || undefined,
        source: formData.source,
        referredBy: formData.referredBy.trim() || undefined,
        currentCompany: formData.currentCompany.trim() || undefined,
        currentTitle: formData.currentTitle.trim() || undefined,
        experienceYears: formData.experienceYears.trim() || undefined,
        noticePeriod: formData.noticePeriod || undefined,
        currentCTC: formData.currentCTC.trim() || undefined,
        expectedSalary: formData.expectedSalary.trim() || undefined,
        resumeUrl: finalResumeUrl,
        resumeFilename: finalResumeFilename,
        notes: formData.notes.trim() || undefined,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        notifyInterviewers: notifyEnabled && notifyEmails.length > 0,
        notifyEmails: notifyEnabled ? notifyEmails : []
      };

      const response = await jobsAPI.addCandidateManually(jobId, payload);
      toastr.success(`${payload.firstName} ${payload.lastName} added successfully`);
      onSuccess(response.data.application);
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to add candidate';
      if (err.response?.status === 409) {
        toastr.warning(errorMsg);
      } else {
        toastr.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="acm-drawer-form">
      <div className="acm-drawer-body">
        {/* Job Context */}
        <div className="acm-job-context">
          Sourcing for <strong>{jobTitle}</strong>
        </div>

        {/* Source Selection */}
        <div className="acm-section">
          <label className="acm-section-label">Source Platform</label>
          <div className="acm-source-grid">
            {SOURCE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                className={`acm-source-chip ${formData.source === opt.value ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, source: opt.value }))}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Basic Info */}
        <div className="acm-section">
          <label className="acm-section-label">Candidate Information</label>
          <div className="acm-row">
            <div className="acm-field">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
                className={errors.firstName ? 'error' : ''}
                autoFocus
              />
              {errors.firstName && <span className="acm-error">{errors.firstName}</span>}
            </div>
            <div className="acm-field">
              <label>Last Name <span className="required">*</span></label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
                className={errors.lastName ? 'error' : ''}
              />
              {errors.lastName && <span className="acm-error">{errors.lastName}</span>}
            </div>
          </div>
          <div className="acm-row">
            <div className="acm-field">
              <label>Email <span className="required">*</span></label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="candidate@email.com"
                className={errors.email ? 'error' : ''}
              />
              {errors.email && <span className="acm-error">{errors.email}</span>}
            </div>
            <div className="acm-field">
              <label>Phone <span className="required">*</span></label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                className={errors.phone ? 'error' : ''}
              />
              {errors.phone && <span className="acm-error">{errors.phone}</span>}
            </div>
          </div>
          <div className="acm-field">
            <label>Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="City, State"
            />
          </div>
        </div>

        {/* Profile Links */}
        <div className="acm-section">
          <label className="acm-section-label">Profile Links</label>
          <div className="acm-field">
            <label>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2" style={{marginRight: '4px', verticalAlign: 'middle'}}>
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn Profile <span className="required">*</span>
            </label>
            <input
              type="url"
              name="linkedinUrl"
              value={formData.linkedinUrl}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/..."
              className={errors.linkedinUrl ? 'error' : ''}
            />
            {errors.linkedinUrl && <span className="acm-error">{errors.linkedinUrl}</span>}
          </div>
          {(formData.source === 'naukri') && (
            <div className="acm-field">
              <label>Naukri Profile</label>
              <input
                type="url"
                name="naukriUrl"
                value={formData.naukriUrl}
                onChange={handleChange}
                placeholder="https://naukri.com/..."
              />
            </div>
          )}
          <div className="acm-row">
            <div className="acm-field">
              <label>GitHub</label>
              <input
                type="url"
                name="githubUrl"
                value={formData.githubUrl}
                onChange={handleChange}
                placeholder="https://github.com/..."
              />
            </div>
            <div className="acm-field">
              <label>Portfolio / Website</label>
              <input
                type="url"
                name="portfolioUrl"
                value={formData.portfolioUrl}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>
          </div>
        </div>

        {/* Professional Details (collapsible) */}
        <div className="acm-section">
          <button
            type="button"
            className="acm-toggle-btn"
            onClick={() => setShowAdditionalFields(!showAdditionalFields)}
          >
            <span>{showAdditionalFields ? '−' : '+'}</span>
            Professional Details &amp; Notes
          </button>
          
          {showAdditionalFields && (
            <div className="acm-additional-fields">
              <div className="acm-row">
                <div className="acm-field">
                  <label>Current Company</label>
                  <input
                    type="text"
                    name="currentCompany"
                    value={formData.currentCompany}
                    onChange={handleChange}
                    placeholder="Company name"
                  />
                </div>
                <div className="acm-field">
                  <label>Current Title</label>
                  <input
                    type="text"
                    name="currentTitle"
                    value={formData.currentTitle}
                    onChange={handleChange}
                    placeholder="Job title"
                  />
                </div>
              </div>
              <div className="acm-row three-col">
                <div className="acm-field">
                  <label>Experience (years)</label>
                  <input
                    type="text"
                    name="experienceYears"
                    value={formData.experienceYears}
                    onChange={handleChange}
                    placeholder="e.g. 5"
                  />
                </div>
                <div className="acm-field">
                  <label>Notice Period</label>
                  <select
                    name="noticePeriod"
                    value={formData.noticePeriod}
                    onChange={handleChange}
                  >
                    <option value="">Select...</option>
                    {NOTICE_PERIOD_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div className="acm-field">
                  <label>Current CTC</label>
                  <input
                    type="text"
                    name="currentCTC"
                    value={formData.currentCTC}
                    onChange={handleChange}
                    placeholder="e.g. 8 LPA"
                  />
                </div>
                <div className="acm-field">
                  <label>Expected CTC</label>
                  <input
                    type="text"
                    name="expectedSalary"
                    value={formData.expectedSalary}
                    onChange={handleChange}
                    placeholder="e.g. 12 LPA"
                  />
                </div>
              </div>

              {formData.source === 'referral' && (
                <div className="acm-field">
                  <label>Referred By</label>
                  <input
                    type="text"
                    name="referredBy"
                    value={formData.referredBy}
                    onChange={handleChange}
                    placeholder="Referrer's name or email"
                  />
                </div>
              )}

              <div className="acm-field">
                <label>Resume</label>
                {uploadedResume ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '6px' }}>
                    <span style={{ fontSize: '0.875rem', color: '#166534', flex: 1 }}>✓ {uploadedResume.filename}</span>
                    <button type="button" onClick={() => { setUploadedResume(null); setResumeFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '0.875rem' }}>Remove</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleResumeFileChange}
                      disabled={uploadingResume}
                      style={{ fontSize: '0.875rem', flex: 1 }}
                    />
                    {uploadingResume && <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>Uploading...</span>}
                  </div>
                )}
                <span className="acm-hint">Or provide a URL below (Google Drive, Dropbox, etc.)</span>
                {!uploadedResume && (
                  <input
                    type="url"
                    name="resumeUrl"
                    value={formData.resumeUrl}
                    onChange={handleChange}
                    placeholder="https://..."
                    style={{ marginTop: '0.375rem' }}
                  />
                )}
              </div>

              <div className="acm-field">
                <label>Tags</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="e.g. pentester, OSCP, senior (comma separated)"
                />
                <span className="acm-hint">Separate multiple tags with commas</span>
              </div>

              <div className="acm-field">
                <label>Recruiter Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any notes about the candidate, conversation details, etc."
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>

        {/* Notify Interviewers / Stakeholders */}
        <div className="acm-section">
          <label className="acm-section-label">
            <input
              type="checkbox"
              checked={notifyEnabled}
              onChange={e => setNotifyEnabled(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            Notify interviewers / stakeholders
          </label>
          {notifyEnabled && (
            <div style={{ marginTop: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="email"
                  value={notifyEmailInput}
                  onChange={e => setNotifyEmailInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNotifyEmail(); } }}
                  placeholder="interviewer@company.com"
                  style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '0.875rem' }}
                />
                <button type="button" onClick={addNotifyEmail} className="acm-btn-cancel" style={{ padding: '0.5rem 1rem' }}>Add</button>
              </div>
              {notifyEmails.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {notifyEmails.map(email => (
                    <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', padding: '0.25rem 0.625rem', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '20px', fontSize: '0.8125rem', color: '#1D4ED8' }}>
                      {email}
                      <button type="button" onClick={() => setNotifyEmails(prev => prev.filter(e => e !== email))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: 0, lineHeight: 1 }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <span className="acm-hint">Press Enter or click Add to add each email. They will receive candidate details and resume link.</span>
            </div>
          )}
        </div>
      </div>

      {/* Sticky footer inside drawer */}
      <div className="acm-drawer-footer">
        <button type="button" className="acm-btn-cancel" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="acm-btn-submit" disabled={submitting}>
          {submitting ? 'Adding...' : 'Add Candidate'}
        </button>
      </div>
    </form>
  );
};

export default AddCandidateDrawerContent;
